/**
 * ReportStage — student writes their own report; Brimi grades it against the expected keywords/ranges.
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CheckCircle2, AlertCircle, Loader2, Sparkles, FileCheck } from "lucide-react";

const SECTIONS = [
  { key: "aim", label: "Aim", placeholder: "Write the objective of your experiment in 1-2 sentences.", rows: 2 },
  { key: "apparatus", label: "Apparatus", placeholder: "List all instruments and materials used, comma-separated.", rows: 2 },
  { key: "procedure", label: "Procedure", placeholder: "Describe the steps you followed, in order.", rows: 5 },
  { key: "result", label: "Result", placeholder: "State your final numerical result with units, e.g. g = 9.75 m/s².", rows: 2 },
  { key: "conclusion", label: "Conclusion", placeholder: "Summarize your findings and how close your result is to the accepted value.", rows: 3 },
];

export const ReportStage = ({ experiment }) => {
  const [sections, setSections] = useState({ aim: "", apparatus: "", procedure: "", result: "", conclusion: "" });
  const [resultValue, setResultValue] = useState("");
  const [grading, setGrading] = useState(false);
  const [grade, setGrade] = useState(null);

  const submit = async () => {
    setGrading(true);
    setGrade(null);
    try {
      const { data } = await api.post(`/experiments/${experiment.id}/report/grade`, {
        sections,
        result_value: resultValue ? parseFloat(resultValue) : null,
      });
      setGrade(data);
    } catch (err) {
      console.error("Report grading failed:", err);
      setGrade({ score: 0, overall: "Could not grade your report — please retry.", sections: {} });
    } finally {
      setGrading(false);
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto overflow-x-hidden p-2 sm:p-4">
      <div className="max-w-4xl mx-auto">
        <div className="rounded-2xl bg-slate-900/70 border border-cyan-500/30 p-5 mb-4">
          <div className="flex items-center gap-2 text-cyan-400 text-xs font-bold uppercase tracking-widest mb-2">
            <FileCheck className="h-3.5 w-3.5" /> Write your report
          </div>
          <p className="text-slate-200 leading-relaxed">
            Write each section in your own words. When you click <b>Grade my report</b>, Brimi will
            compare your work with the expected content and give per-section feedback.
          </p>
        </div>

        <div className="space-y-4">
          {SECTIONS.map((s) => {
            const g = grade?.sections?.[s.key];
            return (
              <div
                key={s.key}
                className={`rounded-2xl border p-5 ${
                  g?.status === "ok" ? "border-emerald-500/50 bg-emerald-950/20"
                  : g?.status === "warn" ? "border-amber-500/50 bg-amber-950/20"
                  : g?.status === "err" ? "border-rose-500/50 bg-rose-950/20"
                  : "border-slate-800 bg-slate-900/40"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <label className="text-slate-100 font-outfit font-bold text-lg">{s.label}</label>
                  {g && (
                    <div className={`flex items-center gap-1.5 text-xs font-bold ${g.status === "ok" ? "text-emerald-400" : g.status === "warn" ? "text-amber-400" : "text-rose-400"}`}>
                      {g.status === "ok" ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                      {g.points}/20
                    </div>
                  )}
                </div>
                {s.key === "result" ? (
                  <div className="flex gap-2">
                    <Textarea
                      value={sections.result}
                      onChange={(e) => setSections((v) => ({ ...v, result: e.target.value }))}
                      placeholder={s.placeholder}
                      className="bg-slate-800 border-slate-700 text-slate-100 flex-1"
                      rows={s.rows}
                      data-testid={`report-input-${s.key}`}
                    />
                    <div className="w-40 space-y-2">
                      <Input
                        type="number"
                        value={resultValue}
                        onChange={(e) => setResultValue(e.target.value)}
                        placeholder="Numeric value"
                        className="bg-slate-800 border-slate-700 text-slate-100"
                        data-testid="report-result-value"
                      />
                      <div className="text-[10px] text-slate-500 font-mono">Enter g value, e.g. 9.75</div>
                    </div>
                  </div>
                ) : (
                  <Textarea
                    value={sections[s.key]}
                    onChange={(e) => setSections((v) => ({ ...v, [s.key]: e.target.value }))}
                    placeholder={s.placeholder}
                    className="bg-slate-800 border-slate-700 text-slate-100"
                    rows={s.rows}
                    data-testid={`report-input-${s.key}`}
                  />
                )}
                {g?.feedback && (
                  <div className={`mt-3 text-sm leading-relaxed ${g.status === "ok" ? "text-emerald-200" : g.status === "warn" ? "text-amber-200" : "text-rose-200"}`}>
                    <b className="uppercase text-xs tracking-widest mr-2">Feedback</b>{g.feedback}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Overall grade */}
        {grade && (
          <div className={`mt-6 rounded-2xl p-6 border-2 ${grade.score >= 80 ? "border-emerald-500 bg-emerald-950/40" : grade.score >= 50 ? "border-amber-500 bg-amber-950/30" : "border-rose-500 bg-rose-950/30"}`} data-testid="report-grade-overall">
            <div className="flex items-center gap-3">
              <Sparkles className={`h-8 w-8 ${grade.score >= 80 ? "text-emerald-400" : grade.score >= 50 ? "text-amber-400" : "text-rose-400"}`} />
              <div>
                <div className={`font-outfit font-black text-4xl ${grade.score >= 80 ? "text-emerald-300" : grade.score >= 50 ? "text-amber-300" : "text-rose-300"}`}>{grade.score}%</div>
                <div className="text-slate-200 mt-1">{grade.overall}</div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <Button
            className="bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-full h-12 px-8 text-base"
            onClick={submit}
            disabled={grading}
            data-testid="report-grade-btn"
          >
            {grading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            {grading ? "Grading..." : grade ? "Regrade my report" : "Grade my report"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportStage;
